// src/services/committeeService.mjs
// Handles all committee- and report-related API calls using the new Phase 2 backend format.

// Use /api prefix for Vite proxy in dev, absolute URL in production
const API_ROOT = import.meta.env.VITE_API_URL || "/api";
const API_BASE = `${API_ROOT}/committees`;

// Helper: attach token if available
function authHeaders() {
  const token = localStorage.getItem("access_token");
  return token
    ? { Authorization: `Bearer ${token}`, "Content-Type": "application/json" }
    : { "Content-Type": "application/json" };
}

/* ---------------- GET: List of committees ---------------- */
export async function getCommittees() {
  try {
    const res = await fetch(`${API_BASE}`, {
      method: "GET",
      headers: authHeaders(),
      credentials: "include",
    });

    if (!res.ok)
      throw new Error(`Failed to load committees (${res.status})`);

    const data = await res.json();

    // ✅ Handle wrapped object or raw array
    if (Array.isArray(data)) return data;
    if (Array.isArray(data.committees)) return data.committees;

    console.warn("Unexpected committees response format:", data);
    return [];
  } catch (err) {
    console.error("Error fetching committees:", err);
    throw err;
  }
}

/* ---------------- GET: Committee report availability ---------------- */
export async function getAvailability(committeeId) {
  try {
    const res = await fetch(
      `${API_BASE}/${committeeId}/reports/availability`,
      {
        method: "GET",
        headers: authHeaders(),
        credentials: "include",
      }
    );

    if (!res.ok)
      throw new Error(`Failed to load availability (${res.status})`);

    const data = await res.json();

    // ✅ Use new field names per backend spec
    return {
      owned_reports: data.owned_reports || [],
      available_reports: data.available_reports || [],
    };
  } catch (err) {
    console.error(`Error fetching availability for ${committeeId}:`, err);
    throw err;
  }
}

/* ---------------- POST: Unlock (attach) a report to the current user ----------------
 * Backend route is still /purchase for URL stability but the semantics are now
 * "attach access". Free/Basic tiers get a 402 with a structured upgrade
 * payload; Max tier and admins succeed with no quota consumed.
 */
export async function purchaseReport(committeeId, reportId) {
  try {
    const res = await fetch(
      `${API_BASE}/${committeeId}/reports/${reportId}/purchase`,
      {
        method: "POST",
        headers: authHeaders(),
        credentials: "include",
      }
    );

    if (res.status === 402) {
      const body = await res.json().catch(() => ({}));
      const detail = body?.detail;
      const message =
        (detail && typeof detail === "object" && detail.message) ||
        (typeof detail === "string" && detail) ||
        "Unlocking reports uploaded by other users requires the Max plan.";
      const err = new Error(message);
      err.upgradeRequired = true;
      err.detail = detail;
      throw err;
    }
    if (res.status === 404) throw new Error("Report not found");
    if (res.status === 409) throw new Error("Already in your library");

    if (!res.ok)
      throw new Error(`Unlock failed (${res.status}) — please try again`);

    return await res.json();
  } catch (err) {
    console.error(`Error unlocking report ${reportId}:`, err);
    throw err;
  }
}

// Semantic alias — new code should prefer this name.
export const unlockReport = purchaseReport;

/* ---------------- GET: Download all reports (CSV or XLSX) ---------------- */
export async function downloadAllReports(committeeId, format = 'csv') {
  try {
    const token = localStorage.getItem('access_token');
    const res = await fetch(
      `${API_BASE}/${committeeId}/download?format=${format}`,
      {
        method: 'GET',
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        credentials: 'include',
      }
    );

    if (!res.ok) {
      const error = await res.json().catch(() => ({}));
      throw new Error(error.detail || `Download failed (${res.status})`);
    }

    // Get filename from Content-Disposition header or use default
    const disposition = res.headers.get('Content-Disposition');
    const filenameMatch = disposition?.match(/filename="?(.+?)"?$/);
    const filename = filenameMatch?.[1] || `committee_${committeeId}.${format === 'csv' ? 'zip' : 'xlsx'}`;

    // Download the file
    const blob = await res.blob();
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);

    return { success: true };
  } catch (err) {
    console.error('Download error:', err);
    throw err;
  }
}

export default {
  getCommittees,
  getAvailability,
  purchaseReport,
  unlockReport,
  downloadAllReports,
};
